import { ReactNode, createContext, useContext } from 'react'
import { Form } from '$components'
import { z } from 'zod'
import { FormStep } from '$components'
import { detailsSchema, DetailsStep } from './details'
import { petsSchema, PetsStep } from './pets'
import { CreateClientFormControls } from './controls'
import { CreateClientFormContent } from './content'
import { FieldErrors } from 'react-hook-form'

export type CreateClientSchema = z.infer<typeof createClientSchema>

export const createClientSchema = z.object({
  ...detailsSchema.shape,
  ...petsSchema.shape,
})

export const steps: FormStep[] = [
  {
    id: 'details',
    label: 'Details',
    content: <DetailsStep />,
    schema: detailsSchema,
  },
  {
    id: 'pets',
    label: 'Pets',
    content: <PetsStep />,
    schema: petsSchema,
  },
]

type CreateClientFormContextType = {
  onSubmit: (values: CreateClientSchema) => void
  onCancel: () => void
  isLoading?: boolean
  errors?: FieldErrors<CreateClientSchema>
}

const CreateClientFormContext = createContext<CreateClientFormContextType | undefined>(undefined)

export const useCreateClientForm = () => {
  const context = useContext(CreateClientFormContext)
  if (!context) {
    throw new Error('useCreateClientForm must be used within a CreateClientFormProvider')
  }
  return context
}

type CreateClientFormProps = CreateClientFormContextType & {
  children?: ReactNode
}

export function CreateClientForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  errors,
}: CreateClientFormProps) {
  const context: CreateClientFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <CreateClientFormContext.Provider value={context}>
      <Form<CreateClientSchema, typeof createClientSchema>
        schema={createClientSchema}
        onSubmit={onSubmit}
        errors={errors}
      >
        {children}
      </Form>
    </CreateClientFormContext.Provider>
  )
}

CreateClientForm.Content = CreateClientFormContent
CreateClientForm.Controls = CreateClientFormControls
